import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  UniversityDemandAnalytics,
  AreaDemandAnalytics,
  StudentDemandSummaryAnalytics,
  StudentDemandRecord,
  Weekday,
} from '../../../types';
import { WEEKDAY_OPTIONS, getWeekdayLabel } from '../../../constants/alexandriaAreas';
import {
  Building2,
  MapPin,
  TrendingUp,
  PieChart,
  Bus,
  CalendarDays,
  Compass,
  Users,
  Search,
  ChevronDown,
  ChevronUp,
  Info,
  Check,
  CheckSquare,
  Square,
  Filter,
  X,
  RotateCcw,
} from 'lucide-react';
import './DemandAnalyticsCharts.css';

// =========================================================
// COLOR PALETTES FOR CHARTS & DONUTS
// =========================================================
const DONUT_COLORS = [
  '#2563eb', // Blue
  '#10b981', // Emerald
  '#f59e0b', // Amber
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#f97316', // Orange
  '#64748b', // Slate
];

// =========================================================
// SUB-COMPONENT: SVG CIRCULAR GAUGE (دائرة بيانية مئوية)
// =========================================================
interface SvgCircularGaugeProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  label?: string;
  sublabel?: string;
  color?: string;
  trackColor?: string;
}

const SvgCircularGauge: React.FC<SvgCircularGaugeProps> = ({
  percentage,
  size = 140,
  strokeWidth = 12,
  label = 'معدل الإشغال',
  sublabel,
  color = '#2563eb',
  trackColor = '#e2e8f0',
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clampedPct = Math.min(100, Math.max(0, percentage));
  const strokeDashoffset = circumference - (clampedPct / 100) * circumference;

  return (
    <div className="gauge-circle-wrapper" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background Track Circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        {/* Animated Filled Arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.16, 1, 0.3, 1)' }}
        />
      </svg>
      {/* Center Percentage Display */}
      <div className="gauge-inner-content">
        <span className="gauge-percentage">{clampedPct}%</span>
        {label && <span className="gauge-caption">{label}</span>}
        {sublabel && <span className="text-[10px] text-slate-400">{sublabel}</span>}
      </div>
    </div>
  );
};

// =========================================================
// SUB-COMPONENT: SVG DONUT CHART (دائرة بيانية لخطوط السير)
// =========================================================
interface DonutSlice {
  id: string;
  label: string;
  value: number;
  percentage: number;
  color: string;
  sublabel?: string;
}

interface SvgDonutChartProps {
  slices: DonutSlice[];
  totalValue: number | string;
  totalLabel: string;
  size?: number;
  thickness?: number;
  onSliceClick?: (slice: DonutSlice) => void;
}

const SvgDonutChart: React.FC<SvgDonutChartProps> = ({
  slices,
  totalValue,
  totalLabel,
  size = 180,
  thickness = 22,
  onSliceClick,
}) => {
  const [hoveredSlice, setHoveredSlice] = useState<DonutSlice | null>(null);

  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = slices.reduce((acc, s) => acc + s.value, 0);

  let accumulatedPercent = 0;

  return (
    <div className="donut-visual-container">
      <div className="donut-svg-wrapper" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Base Empty Ring */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#f1f5f9"
            strokeWidth={thickness}
          />

          {/* Slices */}
          {slices.map((slice) => {
            if (total === 0 || slice.value === 0) return null;
            const slicePercent = slice.value / total;
            const strokeDasharray = `${slicePercent * circumference} ${circumference}`;
            const strokeDashoffset = -accumulatedPercent * circumference;
            accumulatedPercent += slicePercent;

            const isHovered = hoveredSlice?.id === slice.id;

            return (
              <circle
                key={slice.id}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={slice.color}
                strokeWidth={isHovered ? thickness + 4 : thickness}
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={{
                  transition: 'all 0.25s ease',
                  cursor: 'pointer',
                  opacity: hoveredSlice && !isHovered ? 0.6 : 1,
                }}
                onMouseEnter={() => setHoveredSlice(slice)}
                onMouseLeave={() => setHoveredSlice(null)}
                onClick={() => onSliceClick && onSliceClick(slice)}
              />
            );
          })}
        </svg>

        {/* Center Content */}
        <div className="donut-center-text">
          <span className="donut-center-num">
            {hoveredSlice ? hoveredSlice.value : totalValue}
          </span>
          <span className="donut-center-label">
            {hoveredSlice ? hoveredSlice.label : totalLabel}
          </span>
          {hoveredSlice && (
            <span className="text-[10px] font-bold text-blue-600 font-mono">
              {hoveredSlice.percentage}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// =========================================================
// SUB-COMPONENT: MINI CIRCULAR PROGRESS (للكروت والتقويم)
// =========================================================
const SvgMiniCircleProgress: React.FC<{ percentage: number; color?: string; size?: number }> = ({
  percentage,
  color = '#2563eb',
  size = 36,
}) => {
  const strokeWidth = 3.5;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (Math.min(100, Math.max(0, percentage)) / 100) * circumference;

  return (
    <div style={{ width: size, height: size, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#f1f5f9"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
      <span style={{ position: 'absolute', fontSize: '9px', fontWeight: 800, fontFamily: 'monospace', color: '#1e293b' }}>
        {percentage}%
      </span>
    </div>
  );
};

// =========================================================
// MAIN COMPONENT: DemandAnalyticsCharts
// =========================================================
interface DemandAnalyticsChartsProps {
  universities: UniversityDemandAnalytics[];
  areas: AreaDemandAnalytics[];
  summary: StudentDemandSummaryAnalytics;
  allDemandRecords?: StudentDemandRecord[];
  onSelectUniversity?: (uniId: string) => void;
  onSelectArea?: (areaId: string) => void;
}

export const DemandAnalyticsCharts: React.FC<DemandAnalyticsChartsProps> = ({
  universities,
  areas,
  summary,
  allDemandRecords = [],
  onSelectUniversity,
  onSelectArea,
}) => {
  const [metricMode, setMetricMode] = useState<'STUDENTS' | 'ATTENDANCE'>('STUDENTS');
  const [selectedDay, setSelectedDay] = useState<Weekday | 'ALL'>('ALL');
  const [distributionSubTab, setDistributionSubTab] = useState<'ROUTES' | 'STATIONS'>('ROUTES');
  const [routeSearchQuery, setRouteSearchQuery] = useState<string>('');
  const [expandedRouteKey, setExpandedRouteKey] = useState<string | null>(null);
  const [uniSearchQuery, setUniSearchQuery] = useState<string>('');
  const [uniFilterMode, setUniFilterMode] = useState<'ACTIVE_ONLY' | 'ALL'>('ACTIVE_ONLY');
  const [isUnisExpanded, setIsUnisExpanded] = useState<boolean>(false);

  // Selected University IDs for Weekly Calendar & Demand filtering (empty array = All Universities)
  const [selectedUniIds, setSelectedUniIds] = useState<string[]>([]);
  const [isUniDropdownOpen, setIsUniDropdownOpen] = useState<boolean>(false);
  const [uniDropdownSearch, setUniDropdownSearch] = useState<string>('');
  const uniDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (uniDropdownRef.current && !uniDropdownRef.current.contains(event.target as Node)) {
        setIsUniDropdownOpen(false);
      }
    };
    if (isUniDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUniDropdownOpen]);

  const handleToggleUni = (uniId: string) => {
    setSelectedUniIds((prev) => {
      if (prev.includes(uniId)) {
        return prev.filter((id) => id !== uniId);
      } else {
        return [...prev, uniId];
      }
    });
  };

  const handleClearUniSelection = () => {
    setSelectedUniIds([]);
  };

  const handleSelectActiveOnlyUnis = () => {
    const activeIds = universities.filter((u) => u.activeStudents > 0).map((u) => u.universityId);
    setSelectedUniIds(activeIds);
  };

  // Universities filtered by search inside dropdown
  const dropdownFilteredUnis = useMemo(() => {
    let list = [...universities].sort(
      (a, b) => b.activeStudents - a.activeStudents || a.universityName.localeCompare(b.universityName, 'ar')
    );
    if (uniDropdownSearch.trim()) {
      const q = uniDropdownSearch.trim().toLowerCase();
      list = list.filter(
        (u) =>
          u.universityName.toLowerCase().includes(q) ||
          u.governorate.toLowerCase().includes(q)
      );
    }
    return list;
  }, [universities, uniDropdownSearch]);

  // Quick chips: Universities that have active students (or top universities)
  const topQuickUnis = useMemo(() => {
    const activeUnis = universities
      .filter((u) => u.activeStudents > 0)
      .sort((a, b) => b.activeStudents - a.activeStudents);
    if (activeUnis.length > 0) return activeUnis.slice(0, 10);
    return universities.slice(0, 6);
  }, [universities]);

  const selectedUnis = useMemo(() => {
    if (selectedUniIds.length === 0) return universities;
    return universities.filter((u) => selectedUniIds.includes(u.universityId));
  }, [universities, selectedUniIds]);

  const selectedUniNames = useMemo(() => {
    return selectedUnis.map((u) => u.universityName.trim().toLowerCase());
  }, [selectedUnis]);

  // Active records filtered by selected universities
  const activeFilteredRecords = useMemo(() => {
    const all = allDemandRecords || [];
    const activeOnly = all.filter((r) => r.status === 'ACTIVE');
    if (selectedUniIds.length === 0) {
      return activeOnly;
    }
    return activeOnly.filter((r) => {
      if (selectedUniIds.includes(r.universityId)) return true;
      const snapName = (r.universityNameSnapshot || '').trim().toLowerCase();
      return selectedUniNames.includes(snapName);
    });
  }, [allDemandRecords, selectedUniIds, selectedUniNames]);

  // Weekday demand breakdown filtered by university selection
  const effectiveWeekdayBreakdown: Record<Weekday, number> = useMemo(() => {
    if (selectedUniIds.length === 0) {
      return (
        summary.weekdayDemandBreakdown || {
          SATURDAY: 0,
          SUNDAY: 0,
          MONDAY: 0,
          TUESDAY: 0,
          WEDNESDAY: 0,
          THURSDAY: 0,
          FRIDAY: 0,
        }
      );
    }

    if (allDemandRecords && allDemandRecords.length > 0) {
      const counts: Record<Weekday, number> = {
        SATURDAY: 0,
        SUNDAY: 0,
        MONDAY: 0,
        TUESDAY: 0,
        WEDNESDAY: 0,
        THURSDAY: 0,
        FRIDAY: 0,
      };
      activeFilteredRecords.forEach((r) => {
        const days =
          r.preferredTravelDays && r.preferredTravelDays.length > 0
            ? r.preferredTravelDays
            : (['SATURDAY', 'SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY'] as Weekday[]);
        days.forEach((d) => {
          if (counts[d] !== undefined) {
            counts[d] += 1;
          }
        });
      });
      return counts;
    }

    // Fallback using universities list data
    const counts: Record<Weekday, number> = {
      SATURDAY: 0,
      SUNDAY: 0,
      MONDAY: 0,
      TUESDAY: 0,
      WEDNESDAY: 0,
      THURSDAY: 0,
      FRIDAY: 0,
    };
    selectedUnis.forEach((u) => {
      if (u.weekdayDemandBreakdown) {
        (Object.keys(counts) as Weekday[]).forEach((d) => {
          counts[d] += u.weekdayDemandBreakdown?.[d] || 0;
        });
      }
    });
    return counts;
  }, [selectedUniIds, summary.weekdayDemandBreakdown, allDemandRecords, activeFilteredRecords, selectedUnis]);

  // Total active students count filtered by university selection
  const effectiveTotalStudentsCount = useMemo(() => {
    if (selectedUniIds.length === 0) {
      return summary.activeDemandStudents || summary.totalDemandStudents || 0;
    }
    if (allDemandRecords && allDemandRecords.length > 0) {
      return activeFilteredRecords.length;
    }
    return selectedUnis.reduce((acc, u) => acc + (u.activeStudents || 0), 0);
  }, [selectedUniIds, summary, allDemandRecords, activeFilteredRecords, selectedUnis]);

  // Total weekly passenger travel days filtered by university selection
  const effectiveTotalWeeklyPassengers = useMemo(() => {
    return Object.values(effectiveWeekdayBreakdown).reduce((acc, c) => acc + c, 0);
  }, [effectiveWeekdayBreakdown]);

  // Maximum student count across weekdays for proportional bar heights
  const maxEffectiveDayVal = useMemo(() => {
    const counts = WEEKDAY_OPTIONS.map((w) => effectiveWeekdayBreakdown[w.id] || 0);
    return Math.max(...counts, 1);
  }, [effectiveWeekdayBreakdown]);

  // Peak day finder for filtered data
  const effectivePeakDayInfo = useMemo(() => {
    let max = 0;
    let peakDay: Weekday = 'SUNDAY';
    WEEKDAY_OPTIONS.forEach((w) => {
      const val = effectiveWeekdayBreakdown[w.id] || 0;
      if (val > max) {
        max = val;
        peakDay = w.id;
      }
    });
    return { day: peakDay, count: max, label: getWeekdayLabel(peakDay) };
  }, [effectiveWeekdayBreakdown]);

  const effectivePeakDayCount = maxEffectiveDayVal;
  const effectiveEstimatedBusesNeeded = Math.ceil(effectivePeakDayCount / 50) || (effectivePeakDayCount > 0 ? 1 : 0);
  const effectiveWeeklyFleetCapacity = (effectiveEstimatedBusesNeeded || 1) * 50 * 6;
  const effectiveFleetOccupancyRate = Math.min(
    100,
    Math.round((effectiveTotalWeeklyPassengers / (effectiveWeeklyFleetCapacity || 1)) * 100)
  );

  // Active breakdown for selected day or all days (filtered by university)
  const effectiveDailyBreakdown = useMemo(() => {
    if (selectedDay === 'ALL') return null;

    if (selectedUniIds.length === 0) {
      return summary.dailyRouteBreakdown ? summary.dailyRouteBreakdown[selectedDay] : null;
    }

    if (allDemandRecords && allDemandRecords.length > 0) {
      const dayRecords = activeFilteredRecords.filter((r) => {
        const days =
          r.preferredTravelDays && r.preferredTravelDays.length > 0
            ? r.preferredTravelDays
            : (['SATURDAY', 'SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY'] as Weekday[]);
        return days.includes(selectedDay);
      });

      const routeMap: Record<
        string,
        { route: string; pickupStation: string; studentCount: number; studentNames: string[] }
      > = {};

      dayRecords.forEach((r) => {
        const route = r.assignedRoute || `محطة مصر ← ${r.universityNameSnapshot || 'الجامعة'}`;
        const station = r.pickupStation || 'محطة مصر';
        const comboKey = `${route}__${station}`;
        if (!routeMap[comboKey]) {
          routeMap[comboKey] = {
            route,
            pickupStation: station,
            studentCount: 0,
            studentNames: [],
          };
        }
        routeMap[comboKey].studentCount += 1;
        if (r.studentName && !routeMap[comboKey].studentNames.includes(r.studentName)) {
          routeMap[comboKey].studentNames.push(r.studentName);
        }
      });

      const routesList = Object.values(routeMap).sort((a, b) => b.studentCount - a.studentCount);
      return {
        day: selectedDay,
        dayLabelAr: getWeekdayLabel(selectedDay),
        totalStudents: routesList.reduce((acc, curr) => acc + curr.studentCount, 0),
        routes: routesList,
      };
    }

    // Fallback using summary.dailyRouteBreakdown
    if (summary.dailyRouteBreakdown && summary.dailyRouteBreakdown[selectedDay]) {
      const original = summary.dailyRouteBreakdown[selectedDay];
      const filteredRoutes = (original.routes || []).filter((r) => {
        return selectedUnis.some((u) => r.route.toLowerCase().includes(u.universityName.toLowerCase()));
      });
      return {
        ...original,
        totalStudents: filteredRoutes.reduce((acc, curr) => acc + curr.studentCount, 0),
        routes: filteredRoutes,
      };
    }

    return null;
  }, [selectedDay, selectedUniIds, summary.dailyRouteBreakdown, allDemandRecords, activeFilteredRecords, selectedUnis]);

  // Filtered universities based on search query and active/all toggle
  const filteredUnis = useMemo(() => {
    let list = [...universities];
    if (uniFilterMode === 'ACTIVE_ONLY') {
      const activeList = list.filter((u) => u.activeStudents > 0);
      if (activeList.length > 0) {
        list = activeList;
      }
    }
    if (uniSearchQuery.trim()) {
      const q = uniSearchQuery.trim().toLowerCase();
      list = list.filter(
        (u) =>
          u.universityName.toLowerCase().includes(q) ||
          u.governorate.toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) =>
      metricMode === 'STUDENTS'
        ? b.activeStudents - a.activeStudents || b.totalStudents - a.totalStudents || a.universityName.localeCompare(b.universityName, 'ar')
        : b.totalAttendanceDemand - a.totalAttendanceDemand || b.activeStudents - a.activeStudents || a.universityName.localeCompare(b.universityName, 'ar')
    );
  }, [universities, uniFilterMode, uniSearchQuery, metricMode]);

  const maxFilteredUniVal = useMemo(() => {
    if (filteredUnis.length === 0) return 1;
    return metricMode === 'STUDENTS'
      ? Math.max(...filteredUnis.map((u) => u.activeStudents), 1)
      : Math.max(...filteredUnis.map((u) => u.totalAttendanceDemand), 1);
  }, [filteredUnis, metricMode]);

  const displayedUnis = useMemo(() => {
    if (uniSearchQuery.trim() || isUnisExpanded) {
      return filteredUnis;
    }
    return filteredUnis.slice(0, 8);
  }, [filteredUnis, uniSearchQuery, isUnisExpanded]);

  // Top areas
  const sortedAreas = [...areas].sort((a, b) => b.activeStudents - a.activeStudents).slice(0, 8);
  const maxAreaVal = sortedAreas.length > 0 ? sortedAreas[0].activeStudents || 1 : 1;

  // Governorate list from summary
  const governoratesList = Object.entries(summary.governorateBreakdown).sort(
    (a, b) => b[1] - a[1]
  );
  const totalGovCount = governoratesList.reduce((acc, curr) => acc + curr[1], 0) || 1;

  // Baseline total weekly passengers and students across all universities (for comparisons)
  const totalWeeklyPassengers = useMemo(() => {
    return Object.values(summary.weekdayDemandBreakdown || {}).reduce((acc, c) => acc + c, 0);
  }, [summary.weekdayDemandBreakdown]);

  const totalStudentsCount = summary.activeDemandStudents || summary.totalDemandStudents || 26;

  // Donut slices for Top Routes
  const routeDonutSlices: DonutSlice[] = useMemo(() => {
    const routes = summary.routeBreakdown || [];
    if (routes.length === 0) return [];

    const sorted = [...routes].sort((a, b) => b.studentCount - a.studentCount);
    const topSlices = sorted.slice(0, 5);
    const otherStudents = sorted.slice(5).reduce((acc, r) => acc + r.studentCount, 0);

    const totalStudents = sorted.reduce((acc, r) => acc + r.studentCount, 0) || 1;

    const result: DonutSlice[] = topSlices.map((r, idx) => ({
      id: `route_${idx}`,
      label: r.route,
      value: r.studentCount,
      percentage: Math.round((r.studentCount / totalStudents) * 100),
      color: DONUT_COLORS[idx % DONUT_COLORS.length],
      sublabel: `${r.pickupStations.length} نقاط تجمع`,
    }));

    if (otherStudents > 0) {
      result.push({
        id: 'route_others',
        label: 'باقي خطوط السير',
        value: otherStudents,
        percentage: Math.round((otherStudents / totalStudents) * 100),
        color: '#94a3b8',
        sublabel: `${sorted.length - 5} خط سير آخر`,
      });
    }

    return result;
  }, [summary.routeBreakdown]);

  // Donut slices for Top Alexandria Pickup Stations
  const stationDonutSlices: DonutSlice[] = useMemo(() => {
    const stations = summary.pickupStationBreakdown || [];
    if (stations.length === 0) return [];

    const sorted = [...stations].sort((a, b) => b.studentCount - a.studentCount);
    const topSlices = sorted.slice(0, 5);
    const otherStudents = sorted.slice(5).reduce((acc, s) => acc + s.studentCount, 0);

    const totalStudents = sorted.reduce((acc, s) => acc + s.studentCount, 0) || 1;

    const result: DonutSlice[] = topSlices.map((s, idx) => ({
      id: `station_${idx}`,
      label: s.station,
      value: s.studentCount,
      percentage: Math.round((s.studentCount / totalStudents) * 100),
      color: DONUT_COLORS[idx % DONUT_COLORS.length],
      sublabel: `تخدم ${s.routes.length} خطوط سير`,
    }));

    if (otherStudents > 0) {
      result.push({
        id: 'station_others',
        label: 'باقي محطات التجمع',
        value: otherStudents,
        percentage: Math.round((otherStudents / totalStudents) * 100),
        color: '#94a3b8',
        sublabel: `${sorted.length - 5} محطات أخرى`,
      });
    }

    return result;
  }, [summary.pickupStationBreakdown]);

  // Filtered route & pickup station items for the breakdown view
  const filteredDailyRoutes = useMemo(() => {
    if (!effectiveDailyBreakdown) return [];
    let items = effectiveDailyBreakdown.routes || [];
    if (routeSearchQuery.trim()) {
      const q = routeSearchQuery.trim().toLowerCase();
      items = items.filter(
        (r) =>
          r.route.toLowerCase().includes(q) ||
          r.pickupStation.toLowerCase().includes(q) ||
          r.studentNames.some((n) => n.toLowerCase().includes(q))
      );
    }
    return items;
  }, [effectiveDailyBreakdown, routeSearchQuery]);

  const filteredGlobalRoutes = useMemo(() => {
    let items = summary.routeBreakdown || [];
    if (routeSearchQuery.trim()) {
      const q = routeSearchQuery.trim().toLowerCase();
      items = items.filter(
        (r) =>
          r.route.toLowerCase().includes(q) ||
          r.pickupStations.some((s) => s.station.toLowerCase().includes(q))
      );
    }
    return items;
  }, [summary.routeBreakdown, routeSearchQuery]);

  const filteredGlobalStations = useMemo(() => {
    let items = summary.pickupStationBreakdown || [];
    if (routeSearchQuery.trim()) {
      const q = routeSearchQuery.trim().toLowerCase();
      items = items.filter(
        (s) =>
          s.station.toLowerCase().includes(q) ||
          s.routes.some((r) => r.toLowerCase().includes(q))
      );
    }
    return items;
  }, [summary.pickupStationBreakdown, routeSearchQuery]);

  return (
    <div className="demand-analytics-root">
      {/* =========================================================
          TOP METRICS SUMMARY STRIP
          ========================================================= */}
      <div className="analytics-metrics-strip">
        {/* Metric 1: Total Passenger Travel Days */}
        <div className="analytics-metric-card primary">
          <div className="metric-info">
            <span className="metric-label">
              إجمالي أيام السفر المطلوبة
              {selectedUniIds.length > 0 && (
                <span style={{ fontSize: '0.65rem', background: '#fee2e2', color: '#dc2626', padding: '0.1rem 0.35rem', borderRadius: '4px', marginRight: '0.35rem' }}>
                  ({selectedUniIds.length} جامعة)
                </span>
              )}
            </span>
            <div className="metric-value-row">
              <span className="metric-num">{effectiveTotalWeeklyPassengers}</span>
              <span className="metric-unit">يوم سفر / أسبوع</span>
            </div>
            <span className="metric-subtext">
              <TrendingUp size={12} className="text-red-500" />
              <span>
                {selectedUniIds.length > 0 ? 'مخصصة للجامعات المحددة' : 'موزعة على مدار 6 أيام تشغيل'}
              </span>
            </span>
          </div>
          <SvgMiniCircleProgress
            percentage={Math.min(100, Math.round((effectiveTotalWeeklyPassengers / (totalWeeklyPassengers || 1)) * 100))}
            color="#e5232a"
          />
        </div>

        {/* Metric 2: Total Registered Demand Students */}
        <div className="analytics-metric-card blue">
          <div className="metric-info">
            <span className="metric-label">
              إجمالي الطلاب المسجلين
              {selectedUniIds.length > 0 && (
                <span style={{ fontSize: '0.65rem', background: '#dbeafe', color: '#1d4ed8', padding: '0.1rem 0.35rem', borderRadius: '4px', marginRight: '0.35rem' }}>
                  (محدد)
                </span>
              )}
            </span>
            <div className="metric-value-row">
              <span className="metric-num">{effectiveTotalStudentsCount}</span>
              <span className="metric-unit">طالب جامعي</span>
            </div>
            <span className="metric-subtext">
              <Users size={12} className="text-blue-500" />
              <span>
                {selectedUniIds.length > 0
                  ? `من إجمالي ${totalStudentsCount} طالب مسجل`
                  : 'طلاب نشطون بانتظار الرحلات'}
              </span>
            </span>
          </div>
          <SvgMiniCircleProgress
            percentage={Math.min(100, Math.round((effectiveTotalStudentsCount / (totalStudentsCount || 1)) * 100))}
            color="#2563eb"
          />
        </div>

        {/* Metric 3: Fleet Occupancy Rate */}
        <div className="analytics-metric-card emerald">
          <div className="metric-info">
            <span className="metric-label">معدل إشغال الأسطول الأسبوعي</span>
            <div className="metric-value-row">
              <span className="metric-num">{effectiveFleetOccupancyRate}%</span>
              <span className="metric-unit">كفاءة تشغيل</span>
            </div>
            <span className="metric-subtext">
              <Bus size={12} className="text-emerald-500" />
              <span>معدل التشغيل قياساً للحافلات المطلوبة</span>
            </span>
          </div>
          <SvgMiniCircleProgress
            percentage={effectiveFleetOccupancyRate}
            color="#10b981"
          />
        </div>

        {/* Metric 4: Average Attendance Days */}
        <div className="analytics-metric-card amber">
          <div className="metric-info">
            <span className="metric-label">متوسط أيام الحضور لكل طالب</span>
            <div className="metric-value-row">
              <span className="metric-num">
                {effectiveTotalStudentsCount > 0
                  ? (effectiveTotalWeeklyPassengers / effectiveTotalStudentsCount).toFixed(1)
                  : '0'}
              </span>
              <span className="metric-unit">أيام / أسبوع</span>
            </div>
            <span className="metric-subtext">
              <CalendarDays size={12} className="text-amber-500" />
              <span>نمط السفر الأكثر شيوعاً: 3 إلى 4 أيام</span>
            </span>
          </div>
          <SvgMiniCircleProgress
            percentage={Math.min(100, Math.round(((effectiveTotalWeeklyPassengers / (effectiveTotalStudentsCount || 1)) / 6) * 100))}
            color="#f59e0b"
          />
        </div>
      </div>

      {/* =========================================================
          SECTION 1: WEEKLY TRAVEL CALENDAR (تقويم أيام السفر الأسبوعية)
          ========================================================= */}
      <div className="analytics-card">
        <div className="analytics-card-header">
          <div className="analytics-title-group">
            <div className="analytics-icon-badge blue">
              <CalendarDays size={22} />
            </div>
            <div>
              <h3 className="analytics-card-title">
                تقويم أيام السفر الأسبوعية وأعداد الطلبة المقرر سفرهم يومياً
              </h3>
              <p className="analytics-card-subtitle">
                عرض تقويمي تفاعلي لتوزيع حجوزات الطلاب على مدار الأسبوع. انقر على أي يوم لتصفية خطوط سيره وركابه بالأسفل.
              </p>
            </div>
          </div>

          <div className="analytics-header-actions">
            {selectedDay !== 'ALL' && (
              <button
                type="button"
                className="btn-admin-outline"
                style={{ fontSize: '0.75rem', padding: '0.35rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem', borderColor: '#2563eb', color: '#2563eb' }}
                onClick={() => setSelectedDay('ALL')}
              >
                <span>عرض كامل الأسبوع</span>
              </button>
            )}

            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '0.4rem 0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem' }}>
              <span style={{ color: '#64748b' }}>إجمالي أيام السفر المطلوبة:</span>
              <strong style={{ color: '#1d4ed8', fontFamily: 'monospace', fontSize: '0.875rem' }}>{effectiveTotalWeeklyPassengers}</strong>
            </div>
          </div>
        </div>

        {/* =========================================================
            UNIVERSITY FILTER PANEL FOR CALENDAR (تحديد جامعة معينة أو أكثر)
            ========================================================= */}
        <div className="uni-calendar-filter-panel">
          {/* Top Controls Row */}
          <div className="uni-filter-controls-row">
            <div className="uni-filter-label-group">
              <div className="uni-filter-icon">
                <Building2 size={16} />
              </div>
              <span className="uni-filter-label">تصفية أعداد المسافرين بالتقويم حسب الجامعة:</span>
            </div>

            <div className="uni-filter-actions">
              {/* Multi-Select Dropdown Popover */}
              <div className="uni-dropdown-container" ref={uniDropdownRef}>
                <button
                  type="button"
                  className={`uni-dropdown-trigger-btn ${selectedUniIds.length > 0 ? 'active' : ''}`}
                  onClick={() => setIsUniDropdownOpen(!isUniDropdownOpen)}
                  title="تحديد جامعة معينة أو أكثر لحساب أعداد ركابها في التقويم"
                >
                  <Filter size={14} />
                  <span>
                    {selectedUniIds.length === 0
                      ? 'كافة الجامعات (الكل)'
                      : selectedUniIds.length === 1
                      ? selectedUnis[0]?.universityName || 'جامعة محددة'
                      : `${selectedUniIds.length} جامعات محددة`}
                  </span>
                  {selectedUniIds.length > 0 && (
                    <span className="uni-selected-count-badge">
                      {effectiveTotalStudentsCount} طالب
                    </span>
                  )}
                  {isUniDropdownOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>

                {/* Dropdown Menu Popover */}
                {isUniDropdownOpen && (
                  <div className="uni-dropdown-popover">
                    <div className="uni-dropdown-header">
                      <div className="uni-dropdown-search-box">
                        <Search size={14} className="uni-search-icon" />
                        <input
                          type="text"
                          placeholder="ابحث باسم الجامعة أو المحافظة..."
                          value={uniDropdownSearch}
                          onChange={(e) => setUniDropdownSearch(e.target.value)}
                          className="uni-dropdown-search-input"
                          autoFocus
                        />
                        {uniDropdownSearch && (
                          <button
                            type="button"
                            onClick={() => setUniDropdownSearch('')}
                            className="uni-search-clear-btn"
                          >
                            ✕
                          </button>
                        )}
                      </div>

                      <div className="uni-dropdown-quick-actions">
                        <button
                          type="button"
                          className="uni-action-btn select-all"
                          onClick={handleSelectActiveOnlyUnis}
                          title="تحديد كل الجامعات التي لديها طلاب مسجلين"
                        >
                          ذات الإقبال ({universities.filter((u) => u.activeStudents > 0).length})
                        </button>
                        <button
                          type="button"
                          className="uni-action-btn clear-all"
                          onClick={handleClearUniSelection}
                          title="عرض كافة الجامعات بدون تصفية"
                        >
                          عرض كافة الجامعات (الكل)
                        </button>
                      </div>
                    </div>

                    {/* Universities Checkbox List */}
                    <div className="uni-dropdown-list">
                      {dropdownFilteredUnis.length === 0 ? (
                        <div className="uni-dropdown-empty">لا توجد جامعة مطابقة للبحث</div>
                      ) : (
                        dropdownFilteredUnis.map((uni) => {
                          const isSelected = selectedUniIds.includes(uni.universityId);
                          return (
                            <div
                              key={uni.universityId}
                              className={`uni-dropdown-item ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleToggleUni(uni.universityId)}
                            >
                              <div className="uni-item-checkbox">
                                {isSelected ? (
                                  <CheckSquare size={16} className="text-blue-600" />
                                ) : (
                                  <Square size={16} className="text-slate-400" />
                                )}
                              </div>
                              <div className="uni-item-info">
                                <span className="uni-item-name">{uni.universityName}</span>
                                <span className="uni-item-gov">{uni.governorate}</span>
                              </div>
                              <div className="uni-item-meta">
                                {uni.activeStudents > 0 ? (
                                  <span className="uni-item-students-badge">
                                    {uni.activeStudents} طالب
                                  </span>
                                ) : (
                                  <span className="uni-item-zero-badge">0 طالب</span>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    <div className="uni-dropdown-footer">
                      <span className="uni-dropdown-summary-text">
                        {selectedUniIds.length === 0
                          ? 'يتم عرض إجمالي كافة الجامعات'
                          : `محدد: ${selectedUniIds.length} جامعة (${effectiveTotalStudentsCount} طالب)`}
                      </span>
                      <button
                        type="button"
                        className="uni-dropdown-done-btn"
                        onClick={() => setIsUniDropdownOpen(false)}
                      >
                        تطبيق
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Clear button if filtered */}
              {selectedUniIds.length > 0 && (
                <button
                  type="button"
                  className="uni-clear-filter-btn"
                  onClick={handleClearUniSelection}
                  title="إلغاء التصفية والعودة لعرض كافة الجامعات"
                >
                  <RotateCcw size={13} />
                  <span>عرض الكل</span>
                </button>
              )}
            </div>
          </div>

          {/* Quick Selection Chips Scrollable Row */}
          <div className="uni-quick-chips-row">
            {/* Chip 0: All Universities */}
            <button
              type="button"
              className={`uni-chip-pill ${selectedUniIds.length === 0 ? 'selected-all' : ''}`}
              onClick={handleClearUniSelection}
            >
              <span className="uni-chip-title">كافة الجامعات (الكل)</span>
              <span className="uni-chip-count">{summary.activeDemandStudents || summary.totalDemandStudents || 0}</span>
            </button>

            {/* Chips for Top/Active Universities */}
            {topQuickUnis.map((uni) => {
              const isSelected = selectedUniIds.includes(uni.universityId);
              return (
                <button
                  key={uni.universityId}
                  type="button"
                  className={`uni-chip-pill ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleToggleUni(uni.universityId)}
                  title={`${uni.universityName} - ${uni.activeStudents} طالب`}
                >
                  {isSelected && <Check size={12} className="uni-chip-check-icon" />}
                  <span className="uni-chip-title">{uni.universityName}</span>
                  <span className="uni-chip-count">{uni.activeStudents}</span>
                </button>
              );
            })}
          </div>

          {/* Active Filter Notice Banner (when filtered) */}
          {selectedUniIds.length > 0 && (
            <div className="uni-active-banner">
              <div className="uni-active-banner-content">
                <span className="uni-active-banner-label">
                  أنت تشاهد الآن أعداد السفر المخصصة لـ:
                </span>
                <div className="uni-active-tags-list">
                  {selectedUnis.map((u) => (
                    <span key={u.universityId} className="uni-active-tag">
                      <span>{u.universityName}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleUni(u.universityId);
                        }}
                        className="uni-active-tag-remove"
                        title="إزالة هذه الجامعة من التصفية"
                      >
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="uni-active-banner-stats">
                <span>الطلاب المشمولون: <strong>{effectiveTotalStudentsCount} طالب</strong></span>
                <span>•</span>
                <span>إجمالي أيام السفر: <strong>{effectiveTotalWeeklyPassengers} يوم</strong></span>
              </div>
            </div>
          )}
        </div>

        {/* Responsive 8-Pill Weekly Calendar Grid */}
        <div className="weekly-calendar-grid">
          {/* Card 0: Full Week (كامل الأسبوع) */}
          <div
            className={`calendar-pill-card ${selectedDay === 'ALL' ? 'selected' : ''}`}
            onClick={() => setSelectedDay('ALL')}
            title="عرض إجمالي ركاب كافة أيام الأسبوع"
          >
            <div className="calendar-card-header">
              <span className="calendar-day-name">كامل الأسبوع</span>
              <span className="day-badge all">
                {selectedUniIds.length > 0 ? `${selectedUniIds.length} جامعة` : 'الكل'}
              </span>
            </div>

            <div className="calendar-card-body">
              <span className="calendar-passenger-num">{effectiveTotalStudentsCount}</span>
              <span className="calendar-passenger-label">طالب مسجل</span>
            </div>

            <div className="calendar-card-footer">
              <div className="calendar-bus-badge">
                <Users size={12} />
                <span>جميع الأيام</span>
              </div>
              <div className="calendar-mini-progress">
                <div className="calendar-mini-fill" style={{ width: '100%' }}></div>
              </div>
            </div>
          </div>

          {/* Cards 1–7: Days of the Week */}
          {WEEKDAY_OPTIONS.map((dayOpt) => {
            const count = effectiveWeekdayBreakdown[dayOpt.id] || 0;
            const isSelected = selectedDay === dayOpt.id;
            const isFriday = dayOpt.id === 'FRIDAY';
            const isPeak = count >= 20;
            const busesCount = Math.ceil(count / 50) || (count > 0 ? 1 : 0);
            const fillPct = Math.round((count / maxEffectiveDayVal) * 100);

            return (
              <div
                key={dayOpt.id}
                className={`calendar-pill-card ${isSelected ? 'selected' : ''} ${isFriday ? 'is-friday' : ''}`}
                onClick={() => setSelectedDay(dayOpt.id as Weekday)}
                title={`انقر لتصفية ركاب وخطوط يوم ${dayOpt.labelAr}`}
              >
                <div className="calendar-card-header">
                  <span className="calendar-day-name">{dayOpt.labelAr}</span>
                  {isFriday ? (
                    <span className="day-badge holiday">عطلة</span>
                  ) : isPeak ? (
                    <span className="day-badge peak">ذروة 🔥</span>
                  ) : (
                    <span className="day-badge normal">عادي</span>
                  )}
                </div>

                <div className="calendar-card-body">
                  <span className="calendar-passenger-num">{count}</span>
                  <span className="calendar-passenger-label">طالب مسافر</span>
                </div>

                <div className="calendar-card-footer">
                  <div className="calendar-bus-badge">
                    <Bus size={12} />
                    <span>{busesCount > 0 ? `~${busesCount} حافلة` : 'لا رحلات'}</span>
                  </div>
                  <div className="calendar-mini-progress">
                    <div
                      className="calendar-mini-fill"
                      style={{ width: `${fillPct}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* =========================================================
          SECTION 2: OCCUPANCY BAR CHART & CIRCULAR GAUGES
          ========================================================= */}
      <div className="analytics-card">
        <div className="analytics-card-header">
          <div className="analytics-title-group">
            <div className="analytics-icon-badge emerald">
              <TrendingUp size={22} />
            </div>
            <div>
              <h3 className="analytics-card-title">
                رسم بياني لأعداد الطلاب المسافرين كل يوم محدد (معدل الإشغال الأسبوعي)
              </h3>
              <p className="analytics-card-subtitle">
                مقارنة دقيقة لحجم المسافرين يومياً مقارنة بسعة الحافلات القياسية (50 راكباً)، مع مؤشر إشغال الأسطول الأسبوعي.
              </p>
            </div>
          </div>

          <div className="chart-legend-row">
            <div className="legend-badge">
              <span className="legend-dot normal"></span>
              <span>أيام تشغيل عادية</span>
            </div>
            <div className="legend-badge">
              <span className="legend-dot peak"></span>
              <span>أيام الذروة (أكثر من 20 راكب)</span>
            </div>
            <div className="legend-badge">
              <span className="legend-dot friday"></span>
              <span>الجمعة (عطلة رسمية)</span>
            </div>
          </div>
        </div>

        {/* Layout: Circular Gauge Box + Column Bar Chart */}
        <div className="occupancy-chart-layout">
          {/* Left/Start Side: Circular Gauge Widget */}
          <div className="occupancy-gauge-box">
            <SvgCircularGauge
              percentage={effectiveFleetOccupancyRate}
              size={140}
              strokeWidth={14}
              label="إشغال الأسبوع"
              sublabel="قياساً للسعة"
              color={effectiveFleetOccupancyRate > 60 ? '#10b981' : '#2563eb'}
            />

            <div className="gauge-details">
              <div className="gauge-detail-item">
                <span>يوم الذروة الأسبوعي:</span>
                <strong style={{ color: '#b45309' }}>
                  {effectivePeakDayInfo.label} ({effectivePeakDayInfo.count} طالب)
                </strong>
              </div>
              <div className="gauge-detail-item">
                <span>الأسطول اليومي المطلوب:</span>
                <strong>{effectiveEstimatedBusesNeeded} حافلة / باص</strong>
              </div>
              <div className="gauge-detail-item">
                <span>سعة الحافلة الواحدة:</span>
                <strong>50 راكب</strong>
              </div>
            </div>
          </div>

          {/* Right/End Side: Interactive Column Bar Chart */}
          <div className="bar-chart-visual-container">
            <div className="chart-drawing-area">
              {/* Dashed Horizontal Guide Lines */}
              <div className="chart-guide-lines">
                <div className="guide-line">
                  <span className="guide-label">حافلة كاملة (50 راكب)</span>
                </div>
                <div className="guide-line">
                  <span className="guide-label">75% سعة (37 راكب)</span>
                </div>
                <div className="guide-line">
                  <span className="guide-label">نصف حافلة (25 راكب)</span>
                </div>
                <div className="guide-line">
                  <span className="guide-label">25% سعة (12 راكب)</span>
                </div>
                <div className="guide-line" style={{ borderBottomColor: '#cbd5e1' }}>
                  <span className="guide-label" style={{ top: '-8px' }}>0</span>
                </div>
              </div>

              {/* 7 Interactive Day Column Bars */}
              <div className="bar-chart-columns-grid">
                {WEEKDAY_OPTIONS.map((dayOpt) => {
                  const count = effectiveWeekdayBreakdown[dayOpt.id] || 0;
                  const isSelected = selectedDay === dayOpt.id;
                  const isFriday = dayOpt.id === 'FRIDAY';
                  const isPeak = count >= 20;

                  // Normalize height: 50 passengers = 100% height of chart
                  const heightPercent = Math.min(100, Math.max(8, Math.round((count / 50) * 100)));
                  const busesCount = Math.ceil(count / 50) || (count > 0 ? 1 : 0);

                  return (
                    <div
                      key={dayOpt.id}
                      className={`bar-chart-column-item ${isSelected ? 'is-selected' : ''}`}
                      onClick={() => setSelectedDay(dayOpt.id as Weekday)}
                      title={`انقر لاختيار يوم ${dayOpt.labelAr} - ${count} طالب`}
                    >
                      {/* Floating Bubble Badge */}
                      <div className="column-floating-bubble">
                        {count > 0 ? `${count} طالب` : '0'}
                      </div>

                      {/* Track Background */}
                      <div className="column-track-bg">
                        {/* Gradient Filled Bar */}
                        <div
                          className={`column-filled-bar ${
                            isFriday ? 'friday' : isPeak ? 'peak' : 'normal'
                          } ${isSelected ? 'selected-bar' : ''}`}
                          style={{ height: `${heightPercent}%` }}
                        >
                          {heightPercent > 30 && busesCount > 0 && (
                            <span className="column-bus-mini-icon">🚌{busesCount}</span>
                          )}
                        </div>
                      </div>

                      {/* Day Label Below */}
                      <div className="chart-day-label-group">
                        <span className="chart-day-name">{dayOpt.labelAr}</span>
                        <span className="chart-bus-estimate">
                          {busesCount > 0 ? `~${busesCount} باص` : '—'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Hint footer */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.75rem', fontSize: '0.75rem', color: '#64748b' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <Info size={13} className="text-blue-600" />
                <span>انقر على أي عامود بالرسم البياني لتصفية ركاب وخطوط هذا اليوم بالأسفل.</span>
              </span>
              {selectedDay !== 'ALL' && (
                <span style={{ fontWeight: 800, color: '#1d4ed8', background: '#eff6ff', padding: '0.2rem 0.6rem', borderRadius: '8px' }}>
                  اليوم المحدد: {getWeekdayLabel(selectedDay)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          SECTION 3: ROUTE & PICKUP STATIONS DISTRIBUTION (دوائر ورسوم بيانية)
          ========================================================= */}
      <div className="analytics-card">
        <div className="analytics-card-header">
          <div className="analytics-title-group">
            <div className="analytics-icon-badge indigo">
              <Compass size={22} />
            </div>
            <div>
              <h3 className="analytics-card-title">
                {selectedDay === 'ALL'
                  ? 'توزيع الطلاب المسافرين بموقعهم طبقاً لخط السير المحدد ومكان الركوب (كافة الأيام)'
                  : `توزيع الطلاب المسافرين بموقعهم طبقاً لخط السير ومكان الركوب — يوم (${getWeekdayLabel(selectedDay)})`}
              </h3>
              <p className="analytics-card-subtitle">
                توزيع نسبي ورسوم بيانية دائرية لخطوط السير الأكثر طلباً ومحطات الركوب لتسهيل توزيع الحافلات وتنظيم الانطلاق.
              </p>
            </div>
          </div>

          {/* Sub-tab switcher & Search Box */}
          <div className="analytics-header-actions">
            {selectedDay === 'ALL' && (
              <div className="view-subtab-bar">
                <button
                  type="button"
                  className={`subtab-btn ${distributionSubTab === 'ROUTES' ? 'active' : ''}`}
                  onClick={() => setDistributionSubTab('ROUTES')}
                >
                  <Compass size={14} />
                  <span>خطوط السير ({summary.routeBreakdown?.length || 0})</span>
                </button>
                <button
                  type="button"
                  className={`subtab-btn ${distributionSubTab === 'STATIONS' ? 'active' : ''}`}
                  onClick={() => setDistributionSubTab('STATIONS')}
                >
                  <MapPin size={14} />
                  <span>محطات الركوب ({summary.pickupStationBreakdown?.length || 0})</span>
                </button>
              </div>
            )}

            <div className="analytics-search-input-box">
              <input
                type="text"
                placeholder="بحث بخط السير أو المحطة..."
                value={routeSearchQuery}
                onChange={(e) => setRouteSearchQuery(e.target.value)}
                className="analytics-search-input"
              />
              <Search size={14} className="search-icon-pos" />
            </div>
          </div>
        </div>

        {/* Content Mode A: If a specific day is selected */}
        {selectedDay !== 'ALL' ? (
          <div>
            {filteredDailyRoutes.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                <Bus size={36} style={{ margin: '0 auto 0.75rem auto', color: '#94a3b8' }} />
                <h4 style={{ fontSize: '0.875rem', fontWeight: 800, color: '#334155', marginBottom: '0.25rem' }}>
                  لا توجد حجوزات مسجلة ليوم {getWeekdayLabel(selectedDay)}
                </h4>
                <p style={{ fontSize: '0.75rem', color: '#64748b', maxWidth: '360px', margin: '0 auto' }}>
                  لم يتم تسجيل طلبات سفر في هذا اليوم أو لا توجد نتائج مطابقة لبحثك.
                </p>
              </div>
            ) : (
              <div className="day-manifest-grid">
                {filteredDailyRoutes.map((item, idx) => {
                  const comboKey = `${item.route}__${item.pickupStation}`;
                  const isExpanded = expandedRouteKey === comboKey;
                  const busSharePct = Math.min(100, Math.round((item.studentCount / 50) * 100));

                  return (
                    <div key={`${comboKey}_${idx}`} className="manifest-card">
                      <div className="manifest-header">
                        <div>
                          <span style={{ fontSize: '0.6875rem', fontWeight: 800, color: '#4f46e5', display: 'block', marginBottom: '0.25rem' }}>
                            خط السير المقترح
                          </span>
                          <h4 className="manifest-route-name">{item.route}</h4>
                        </div>

                        <div style={{ textAlign: 'center', background: '#eef2ff', border: '1px solid #c7d2fe', padding: '0.35rem 0.65rem', borderRadius: '10px' }}>
                          <span style={{ fontSize: '1.125rem', fontWeight: 900, fontFamily: 'monospace', color: '#3730a3', display: 'block', lineHeight: 1 }}>
                            {item.studentCount}
                          </span>
                          <span style={{ fontSize: '0.625rem', color: '#4f46e5' }}>طالب</span>
                        </div>
                      </div>

                      <div className="manifest-station-pill">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <MapPin size={13} className="text-emerald-600" />
                          <span style={{ color: '#64748b' }}>مكان الركوب:</span>
                          <strong style={{ color: '#0f172a' }}>{item.pickupStation}</strong>
                        </div>
                        <span style={{ fontSize: '0.6875rem', fontFamily: 'monospace', color: '#059669', background: '#ecfdf5', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>
                          {busSharePct}% من الباص
                        </span>
                      </div>

                      <div className="manifest-load-bar">
                        <div className="manifest-load-header">
                          <span>نسبة إشغال مقاعد الحافلة:</span>
                          <strong>{item.studentCount} / 50 راكب</strong>
                        </div>
                        <div className="manifest-track">
                          <div className="manifest-fill" style={{ width: `${busSharePct}%` }}></div>
                        </div>
                      </div>

                      {/* Expandable student roster */}
                      <div>
                        <button
                          type="button"
                          className="manifest-accordion-btn"
                          onClick={() => setExpandedRouteKey(isExpanded ? null : comboKey)}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <Users size={13} className="text-indigo-600" />
                            <span>قائمة الطلاب المسافرين ({item.studentNames.length})</span>
                          </span>
                          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>

                        {isExpanded && (
                          <div className="manifest-students-dropdown">
                            {item.studentNames.map((name, sIdx) => (
                              <div key={sIdx} className="student-roster-row">
                                <span style={{ fontWeight: 700, color: '#1e293b' }}>{name}</span>
                                <span style={{ color: '#94a3b8', fontFamily: 'monospace' }}>#{sIdx + 1}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          /* Content Mode B: All Days Distribution (Donut Chart + Ranked List) */
          <div>
            {distributionSubTab === 'ROUTES' ? (
              <div className="routes-distribution-layout">
                {/* Left Side: Donut Chart for Routes */}
                <div className="donut-card-box">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.8125rem', fontWeight: 800, color: '#0f172a' }}>
                      الدائرة البيانية لخطوط السير
                    </span>
                    <span style={{ fontSize: '0.6875rem', color: '#64748b' }}>حسب نسبة الركاب</span>
                  </div>

                  <SvgDonutChart
                    slices={routeDonutSlices}
                    totalValue={summary.routeBreakdown?.length || 0}
                    totalLabel="خط سير"
                    size={190}
                    thickness={24}
                  />

                  {/* Donut Legend */}
                  <div className="donut-legend-list">
                    {routeDonutSlices.map((slice) => (
                      <div key={slice.id} className="donut-legend-row">
                        <div className="donut-legend-title">
                          <span className="donut-color-dot" style={{ backgroundColor: slice.color }}></span>
                          <span className="donut-legend-name" title={slice.label}>
                            {slice.label}
                          </span>
                        </div>
                        <div className="donut-legend-stats">
                          <span className="donut-legend-count">{slice.value} طالب</span>
                          <span className="donut-legend-pct">{slice.percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right Side: Ranked Route Cards */}
                <div className="ranked-routes-container">
                  {filteredGlobalRoutes.length === 0 ? (
                    <p style={{ fontSize: '0.8125rem', color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
                      لا توجد خطوط سير مطابقة لكلمات البحث
                    </p>
                  ) : (
                    filteredGlobalRoutes.map((r, idx) => {
                      const sharePct = r.percentage || 0;
                      return (
                        <div key={idx} className="ranked-route-card">
                          <div className="route-card-top-row">
                            <div className="route-rank-and-name">
                              <span
                                className={`route-rank-badge ${
                                  idx === 0 ? 'top-1' : idx === 1 ? 'top-2' : idx === 2 ? 'top-3' : ''
                                }`}
                              >
                                #{idx + 1}
                              </span>
                              <div>
                                <h4 className="route-name-title">{r.route}</h4>
                                <span className="route-sub-stops">
                                  تخدم {r.pickupStations.length} نقاط تجمع ومحطات ركوب
                                </span>
                              </div>
                            </div>

                            <div className="route-metrics-badge">
                              <span className="route-pct-tag">{sharePct}%</span>
                              <span className="route-passengers-pill">
                                <Users size={12} />
                                <span>{r.studentCount} طالب</span>
                              </span>
                            </div>
                          </div>

                          {/* Progress Meter Bar */}
                          <div className="route-progress-track">
                            <div
                              className="route-progress-fill"
                              style={{ width: `${Math.min(100, sharePct * 2.5)}%` }}
                            ></div>
                          </div>

                          {/* Pickup Stations Chips */}
                          <div className="route-stations-row">
                            {r.pickupStations.map((st, sIdx) => (
                              <span key={sIdx} className="station-badge-chip">
                                <MapPin size={10} className="text-emerald-600" />
                                <span>{st.station}:</span>
                                <strong>{st.count}</strong>
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ) : (
              /* Sub-Tab 2: Alexandria Pickup Stations */
              <div className="routes-distribution-layout">
                {/* Left Side: Donut Chart for Stations */}
                <div className="donut-card-box">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.8125rem', fontWeight: 800, color: '#0f172a' }}>
                      الدائرة البيانية لنقاط الركوب
                    </span>
                    <span style={{ fontSize: '0.6875rem', color: '#64748b' }}>بالإسكندرية</span>
                  </div>

                  <SvgDonutChart
                    slices={stationDonutSlices}
                    totalValue={summary.pickupStationBreakdown?.length || 0}
                    totalLabel="محطة ركوب"
                    size={190}
                    thickness={24}
                  />

                  {/* Donut Legend */}
                  <div className="donut-legend-list">
                    {stationDonutSlices.map((slice) => (
                      <div key={slice.id} className="donut-legend-row">
                        <div className="donut-legend-title">
                          <span className="donut-color-dot" style={{ backgroundColor: slice.color }}></span>
                          <span className="donut-legend-name" title={slice.label}>
                            {slice.label}
                          </span>
                        </div>
                        <div className="donut-legend-stats">
                          <span className="donut-legend-count">{slice.value} طالب</span>
                          <span className="donut-legend-pct">{slice.percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right Side: Ranked Station Cards */}
                <div className="ranked-routes-container">
                  {filteredGlobalStations.length === 0 ? (
                    <p style={{ fontSize: '0.8125rem', color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
                      لا توجد محطات ركوب مطابقة للبحث
                    </p>
                  ) : (
                    filteredGlobalStations.map((s, idx) => {
                      const sharePct = s.percentage || 0;
                      return (
                        <div key={idx} className="ranked-route-card">
                          <div className="route-card-top-row">
                            <div className="route-rank-and-name">
                              <span
                                className={`route-rank-badge ${
                                  idx === 0 ? 'top-1' : idx === 1 ? 'top-2' : idx === 2 ? 'top-3' : ''
                                }`}
                              >
                                #{idx + 1}
                              </span>
                              <div>
                                <h4 className="route-name-title">{s.station}</h4>
                                <span className="route-sub-stops">
                                  نقطة تجمع تخدم {s.routes.length} خط سير مختلف
                                </span>
                              </div>
                            </div>

                            <div className="route-metrics-badge">
                              <span className="route-pct-tag" style={{ color: '#059669' }}>
                                {sharePct}%
                              </span>
                              <span
                                className="route-passengers-pill"
                                style={{ background: '#ecfdf5', borderColor: '#a7f3d0', color: '#065f46' }}
                              >
                                <Users size={12} />
                                <span>{s.studentCount} طالب</span>
                              </span>
                            </div>
                          </div>

                          {/* Progress Meter Bar */}
                          <div className="route-progress-track">
                            <div
                              className="route-progress-fill"
                              style={{
                                width: `${Math.min(100, sharePct * 2.5)}%`,
                                background: 'linear-gradient(90deg, #10b981, #059669)',
                              }}
                            ></div>
                          </div>

                          {/* Routes served */}
                          <div className="route-stations-row">
                            {s.routes.map((rt, rIdx) => (
                              <span key={rIdx} className="station-badge-chip">
                                <Compass size={10} className="text-blue-600" />
                                <span>{rt}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* =========================================================
          SECTION 4: UNIVERSITY & RESIDENTIAL AREA DEMAND GRIDS
          ========================================================= */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '1.5rem' }}>
        {/* Demand by University */}
        <div className="analytics-card">
          <div className="analytics-card-header">
            <div className="analytics-title-group">
              <div className="analytics-icon-badge blue">
                <Building2 size={20} />
              </div>
              <div>
                <h3 className="analytics-card-title">حجم الطلب حسب الجامعات المستهدفة</h3>
                <p className="analytics-card-subtitle">الجامعات والمقاصد التعليمية الأكثر طلباً</p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.25rem', background: '#f1f5f9', padding: '0.25rem', borderRadius: '10px', fontSize: '0.75rem' }}>
              <button
                type="button"
                style={{
                  padding: '0.3rem 0.65rem',
                  borderRadius: '6px',
                  fontWeight: 700,
                  border: 'none',
                  background: metricMode === 'STUDENTS' ? '#ffffff' : 'transparent',
                  color: metricMode === 'STUDENTS' ? '#2563eb' : '#64748b',
                  boxShadow: metricMode === 'STUDENTS' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                  cursor: 'pointer',
                }}
                onClick={() => setMetricMode('STUDENTS')}
              >
                عدد الطلاب
              </button>
              <button
                type="button"
                style={{
                  padding: '0.3rem 0.65rem',
                  borderRadius: '6px',
                  fontWeight: 700,
                  border: 'none',
                  background: metricMode === 'ATTENDANCE' ? '#ffffff' : 'transparent',
                  color: metricMode === 'ATTENDANCE' ? '#2563eb' : '#64748b',
                  boxShadow: metricMode === 'ATTENDANCE' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                  cursor: 'pointer',
                }}
                onClick={() => setMetricMode('ATTENDANCE')}
              >
                أيام الحضور
              </button>
            </div>
          </div>

          {/* Search & Filter Bar for Universities */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', marginTop: '0.75rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: '180px' }}>
                <Search
                  size={15}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#94a3b8',
                    pointerEvents: 'none',
                  }}
                />
                <input
                  type="text"
                  value={uniSearchQuery}
                  onChange={(e) => setUniSearchQuery(e.target.value)}
                  placeholder="ابحث باسم الجامعة أو المحافظة..."
                  style={{
                    width: '100%',
                    padding: '0.45rem 2.25rem 0.45rem 0.75rem',
                    fontSize: '0.8125rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    outline: 'none',
                    background: '#f8fafc',
                    transition: 'all 0.2s',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.background = '#ffffff';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.background = '#f8fafc';
                  }}
                />
                {uniSearchQuery && (
                  <button
                    type="button"
                    onClick={() => setUniSearchQuery('')}
                    style={{
                      position: 'absolute',
                      left: '0.5rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      padding: '0.2rem',
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.25rem', background: '#f8fafc', padding: '0.2rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <button
                  type="button"
                  onClick={() => setUniFilterMode('ACTIVE_ONLY')}
                  style={{
                    padding: '0.3rem 0.6rem',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    border: 'none',
                    background: uniFilterMode === 'ACTIVE_ONLY' ? '#2563eb' : 'transparent',
                    color: uniFilterMode === 'ACTIVE_ONLY' ? '#ffffff' : '#64748b',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                  title="عرض الجامعات التي تم تسجيل طلاب عليها فقط"
                >
                  ذات الإقبال ({universities.filter((u) => u.activeStudents > 0).length})
                </button>
                <button
                  type="button"
                  onClick={() => setUniFilterMode('ALL')}
                  style={{
                    padding: '0.3rem 0.6rem',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    border: 'none',
                    background: uniFilterMode === 'ALL' ? '#2563eb' : 'transparent',
                    color: uniFilterMode === 'ALL' ? '#ffffff' : '#64748b',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                  title="عرض كافة الجامعات المسجلة في خيارات التسجيل"
                >
                  كافة الجامعات ({universities.length})
                </button>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
            {displayedUnis.length === 0 ? (
              <div style={{ padding: '2rem 1rem', textAlign: 'center', color: '#64748b', fontSize: '0.875rem', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                لا توجد جامعات مطابقة للبحث المحدد
              </div>
            ) : (
              displayedUnis.map((u) => {
                const val = metricMode === 'STUDENTS' ? u.activeStudents : u.totalAttendanceDemand;
                const pct = u.activeStudents > 0 ? Math.min(100, Math.max(8, Math.round((val / maxFilteredUniVal) * 100))) : 2;

                return (
                  <div
                    key={u.universityId}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.35rem',
                      cursor: 'pointer',
                      padding: '0.5rem 0.65rem',
                      borderRadius: '8px',
                      background: u.activeStudents > 0 ? 'transparent' : '#fcfcfd',
                      transition: 'background 0.15s ease',
                    }}
                    onClick={() => onSelectUniversity && onSelectUniversity(u.universityId)}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = u.activeStudents > 0 ? 'transparent' : '#fcfcfd')}
                    title="اضغط للتفاصيل والتحليل التفصيلي للجامعة"
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8125rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 800, color: u.activeStudents > 0 ? '#0f172a' : '#475569' }}>
                          {u.universityName}
                        </span>
                        <span style={{ fontSize: '0.6875rem', color: '#94a3b8' }}>({u.governorate})</span>
                        {selectedUniIds.includes(u.universityId) && (
                          <span className="uni-cal-filter-active-pill">
                            محددة بالتقويم ✓
                          </span>
                        )}
                        {u.activeStudents >= 1 ? (
                          <span style={{ fontSize: '0.6875rem', background: '#eff6ff', color: '#1d4ed8', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: 800 }}>
                            ~{Math.ceil(u.activeStudents / 50) || 1} باص
                          </span>
                        ) : (
                          <span style={{ fontSize: '0.65rem', background: '#f1f5f9', color: '#64748b', padding: '0.05rem 0.35rem', borderRadius: '4px', fontWeight: 600 }}>
                            متاحة للاختيار
                          </span>
                        )}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontWeight: 800, color: u.activeStudents > 0 ? '#1e293b' : '#94a3b8', fontFamily: 'monospace' }}>
                          {metricMode === 'STUDENTS'
                            ? `${u.activeStudents} طالب`
                            : `${u.totalAttendanceDemand.toLocaleString('ar-EG')} يوم`}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleUni(u.universityId);
                          }}
                          style={{
                            background: selectedUniIds.includes(u.universityId) ? '#eff6ff' : '#f8fafc',
                            border: `1px solid ${selectedUniIds.includes(u.universityId) ? '#bfdbfe' : '#e2e8f0'}`,
                            borderRadius: '6px',
                            padding: '0.15rem 0.45rem',
                            fontSize: '0.6875rem',
                            fontWeight: 700,
                            color: selectedUniIds.includes(u.universityId) ? '#1d4ed8' : '#64748b',
                            cursor: 'pointer',
                          }}
                          title="تصفية أو إلغاء تصفية التقويم على هذه الجامعة"
                        >
                          {selectedUniIds.includes(u.universityId) ? 'إلغاء' : 'تصفية'}
                        </button>
                      </div>
                    </div>

                    <div style={{ width: '100%', height: '7px', background: '#f1f5f9', borderRadius: '9999px', overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%',
                          borderRadius: '9999px',
                          background: u.activeStudents > 0
                            ? 'linear-gradient(90deg, #3b82f6, #60a5fa)'
                            : '#e2e8f0',
                          width: `${pct}%`,
                          transition: 'width 0.6s ease',
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })
            )}

            {/* Expand / Collapse Button if more than 8 universities */}
            {!uniSearchQuery.trim() && filteredUnis.length > 8 && (
              <button
                type="button"
                onClick={() => setIsUnisExpanded(!isUnisExpanded)}
                style={{
                  width: '100%',
                  marginTop: '0.5rem',
                  padding: '0.55rem',
                  borderRadius: '8px',
                  border: '1px dashed #cbd5e1',
                  background: '#f8fafc',
                  color: '#2563eb',
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.35rem',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#eff6ff')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#f8fafc')}
              >
                {isUnisExpanded ? (
                  <>
                    <ChevronUp size={16} />
                    <span>عرض الأبرز فقط</span>
                  </>
                ) : (
                  <>
                    <ChevronDown size={16} />
                    <span>عرض كافة الجامعات ({filteredUnis.length} جامعة)</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Demand by Alexandria Residential Area */}
        <div className="analytics-card">
          <div className="analytics-card-header">
            <div className="analytics-title-group">
              <div className="analytics-icon-badge emerald">
                <MapPin size={20} />
              </div>
              <div>
                <h3 className="analytics-card-title">توزيع الطلب حسب مناطق الإسكندرية</h3>
                <p className="analytics-card-subtitle">أعلى مناطق التركز السكني للطلاب المسجلين</p>
              </div>
            </div>

            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#059669', background: '#ecfdf5', padding: '0.2rem 0.6rem', borderRadius: '8px' }}>
              المناطق السكنية
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
            {sortedAreas.map((a) => {
              const pct = Math.min(100, Math.round((a.activeStudents / maxAreaVal) * 100));
              const share = Math.round((a.activeStudents / (summary.activeDemandStudents || 1)) * 100);

              return (
                <div
                  key={a.areaId}
                  style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', cursor: 'pointer' }}
                  onClick={() => onSelectArea && onSelectArea(a.areaId)}
                  title="اضغط لمعرفة رغبات طلاب هذه المنطقة"
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8125rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontWeight: 800, color: '#0f172a' }}>{a.areaName}</span>
                      <span style={{ fontSize: '0.6875rem', color: '#94a3b8' }}>({a.areaGroupName})</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#10b981', fontFamily: 'monospace' }}>
                        {share}%
                      </span>
                      <span style={{ fontWeight: 800, color: '#1e293b', fontFamily: 'monospace' }}>
                        {a.activeStudents} طالب
                      </span>
                    </div>
                  </div>

                  <div style={{ width: '100%', height: '8px', background: '#f1f5f9', borderRadius: '9999px', overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        borderRadius: '9999px',
                        background: 'linear-gradient(90deg, #10b981, #34d399)',
                        width: `${pct}%`,
                        transition: 'width 0.6s ease',
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* =========================================================
          SECTION 5: GOVERNORATE DISTRIBUTION (دوائر ونسب مئوية)
          ========================================================= */}
      <div className="analytics-card">
        <div className="analytics-card-header">
          <div className="analytics-title-group">
            <div className="analytics-icon-badge amber">
              <PieChart size={20} />
            </div>
            <div>
              <h3 className="analytics-card-title">التوزيع الجغرافي لمحافظات الجامعات المقصد</h3>
              <p className="analytics-card-subtitle">نسبة وتوزيع الطلاب حسب المحافظة المستهدفة</p>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
          {governoratesList.map(([gov, count], idx) => {
            const pct = Math.round((count / totalGovCount) * 100);
            const color = DONUT_COLORS[idx % DONUT_COLORS.length];

            return (
              <div
                key={gov}
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '14px',
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                }}
              >
                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>
                    {gov}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.35rem' }}>
                    <span style={{ fontSize: '1.25rem', fontWeight: 900, fontFamily: 'monospace', color: '#0f172a' }}>
                      {count}
                    </span>
                    <span style={{ fontSize: '0.6875rem', color: '#94a3b8' }}>طالب</span>
                  </div>
                </div>

                <SvgMiniCircleProgress percentage={pct} color={color} size={42} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
